import React, { useState, useEffect } from "react";
import { useBusiness, api } from "@/App";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";

const AllocationCalculator = () => {
  const { selectedBusiness } = useBusiness();
  const [revenue, setRevenue] = useState("");
  const [notes, setNotes] = useState("");
  const [allocations, setAllocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculatedAllocation, setCalculatedAllocation] = useState(null);

  useEffect(() => {
    if (selectedBusiness) {
      fetchAllocations();
    }
  }, [selectedBusiness]);

  const fetchAllocations = async () => {
    try {
      const response = await api.get(`/allocations?business_id=${selectedBusiness.id}`);
      setAllocations(response.data);
    } catch (error) {
      console.error("Failed to fetch allocations:", error);
    }
  };

  const calculateAllocation = () => {
    const amount = parseFloat(revenue);
    if (isNaN(amount) || amount <= 0) {
      toast.error("Please enter a valid revenue amount");
      return;
    }

    const profit = amount * (selectedBusiness.profit_percent / 100);
    const tax = amount * (selectedBusiness.tax_percent / 100);
    const ownerPay = amount * (selectedBusiness.owner_pay_percent / 100);
    const operating = amount * (selectedBusiness.operating_expense_percent / 100);

    setCalculatedAllocation({
      total: amount,
      profit: profit.toFixed(2),
      tax: tax.toFixed(2),
      ownerPay: ownerPay.toFixed(2),
      operating: operating.toFixed(2),
    });
  };

  const saveAllocation = async () => {
    if (!calculatedAllocation) {
      toast.error("Please calculate an allocation first");
      return;
    }

    setLoading(true);
    try {
      await api.post("/allocations", {
        business_id: selectedBusiness.id,
        total_revenue: calculatedAllocation.total,
        notes: notes || null,
      });
      toast.success("Allocation saved successfully!");
      setRevenue("");
      setNotes("");
      setCalculatedAllocation(null);
      fetchAllocations();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save allocation");
    } finally {
      setLoading(false);
    }
  };

  const exportAllocations = async () => {
    try {
      const response = await api.get(`/allocations/export?business_id=${selectedBusiness.id}`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `allocations_${selectedBusiness.name}_${new Date().toISOString().split("T")[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Export downloaded!");
    } catch (error) {
      toast.error("Failed to export allocations");
    }
  };

  if (!selectedBusiness) {
    return (
      <div className="text-center py-12">
        <p className="text-muted-foreground">Please select or create a business first</p>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="allocation-page">
      <div>
        <h1 className="text-3xl font-bold text-stone-900">Revenue Allocation Calculator</h1>
        <p className="text-stone-600">Calculate and save your Opturna allocations</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Calculator */}
        <Card>
          <CardHeader>
            <CardTitle>New Allocation</CardTitle>
            <CardDescription>
              Enter the total revenue received since your last allocation
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Current Percentages */}
            <div className="grid grid-cols-4 gap-4 p-4 rounded-xl bg-stone-50">
              <div className="text-center">
                <p className="text-2xl font-bold text-[#1e3a5f]">{selectedBusiness.profit_percent}%</p>
                <p className="text-xs text-stone-500">Profit</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-[#1e3a5f]">{selectedBusiness.tax_percent}%</p>
                <p className="text-xs text-stone-500">Tax</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-[#3d7abd]">{selectedBusiness.owner_pay_percent}%</p>
                <p className="text-xs text-stone-500">Owner Pay</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-[#6b9fd4]">{selectedBusiness.operating_expense_percent}%</p>
                <p className="text-xs text-stone-500">Operating</p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="revenue">Total Revenue</Label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-500">$</span>
                <Input
                  id="revenue"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="0.00"
                  value={revenue}
                  onChange={(e) => {
                    setRevenue(e.target.value);
                    setCalculatedAllocation(null);
                  }}
                  className="pl-8 h-14 text-2xl font-mono"
                  data-testid="revenue-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="Add any notes about this allocation..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                data-testid="notes-input"
              />
            </div>

            <Button
              onClick={calculateAllocation}
              variant="outline"
              className="w-full"
              data-testid="calculate-btn"
            >
              Calculate Allocation
            </Button>

            {calculatedAllocation && (
              <div className="space-y-4 animate-fade-in">
                <div className="p-4 rounded-xl bg-[#1e3a5f]/5 border border-[#1e3a5f]/20">
                  <h3 className="font-semibold text-[#1e3a5f] mb-4">Allocation Results</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 rounded-lg bg-white">
                      <p className="text-sm text-stone-500">Profit Account</p>
                      <p className="text-xl font-bold text-[#1e3a5f]" data-testid="calc-profit">
                        ${parseFloat(calculatedAllocation.profit).toLocaleString()}
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-white">
                      <p className="text-sm text-stone-500">Tax Account</p>
                      <p className="text-xl font-bold text-[#1e3a5f]" data-testid="calc-tax">
                        ${parseFloat(calculatedAllocation.tax).toLocaleString()}
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-white">
                      <p className="text-sm text-stone-500">Owner Pay Account</p>
                      <p className="text-xl font-bold text-[#3d7abd]" data-testid="calc-owner">
                        ${parseFloat(calculatedAllocation.ownerPay).toLocaleString()}
                      </p>
                    </div>
                    <div className="p-3 rounded-lg bg-white">
                      <p className="text-sm text-stone-500">Operating Account</p>
                      <p className="text-xl font-bold text-[#6b9fd4]" data-testid="calc-opex">
                        ${parseFloat(calculatedAllocation.operating).toLocaleString()}
                      </p>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={saveAllocation}
                  disabled={loading}
                  className="w-full bg-[#1e3a5f] hover:bg-[#2d5a8e]"
                  data-testid="save-allocation-btn"
                >
                  {loading ? "Saving..." : "Save Allocation"}
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tips Card */}
        <Card>
          <CardHeader>
            <CardTitle>Opturna Rules</CardTitle>
            <CardDescription>Follow these guidelines for success</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex gap-4 p-4 rounded-xl bg-amber-50 border border-amber-200">
              <div className="text-2xl">⚠️</div>
              <div>
                <h4 className="font-semibold text-amber-800">Do NOT pay expenses from Income</h4>
                <p className="text-sm text-amber-700">
                  Always transfer money to your Operating Expense account first, then pay bills from there.
                </p>
              </div>
            </div>

            <div className="flex gap-4 p-4 rounded-xl bg-[#1e3a5f]/5 border border-[#1e3a5f]/20">
              <div className="text-2xl">🔒</div>
              <div>
                <h4 className="font-semibold text-[#1e3a5f]">Profit & Tax accounts are untouchable</h4>
                <p className="text-sm text-[#1e3a5f]">
                  These accounts are for savings only. Don't use them for operational expenses.
                </p>
              </div>
            </div>

            <div className="flex gap-4 p-4 rounded-xl bg-blue-50 border border-blue-200">
              <div className="text-2xl">✂️</div>
              <div>
                <h4 className="font-semibold text-blue-800">Tight on Operating? Cut expenses</h4>
                <p className="text-sm text-blue-700">
                  If your Operating account feels tight, reduce expenses—don't reduce your allocations.
                </p>
              </div>
            </div>

            <div className="flex gap-4 p-4 rounded-xl bg-purple-50 border border-purple-200">
              <div className="text-2xl">📅</div>
              <div>
                <h4 className="font-semibold text-purple-800">Quarterly: Distribute 50% of Profit</h4>
                <p className="text-sm text-purple-700">
                  Each quarter, take 50% of your Profit account as an owner distribution.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Allocation History */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Allocation History</CardTitle>
              <CardDescription>Past revenue allocations for {selectedBusiness.name}</CardDescription>
            </div>
            {allocations.length > 0 && (
              <Button variant="outline" onClick={exportAllocations} data-testid="export-btn">
                Export CSV
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {allocations.length > 0 ? (
            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50">
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Revenue</TableHead>
                    <TableHead className="text-right">Profit</TableHead>
                    <TableHead className="text-right">Tax</TableHead>
                    <TableHead className="text-right">Owner Pay</TableHead>
                    <TableHead className="text-right">Operating</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {allocations.map((allocation) => (
                    <TableRow key={allocation.id}>
                      <TableCell className="font-medium">
                        {new Date(allocation.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        ${allocation.total_revenue.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-mono text-[#1e3a5f]">
                        ${allocation.profit_amount.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        ${allocation.tax_amount.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        ${allocation.owner_pay_amount.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-right font-mono">
                        ${allocation.operating_expense_amount.toLocaleString()}
                      </TableCell>
                      <TableCell className="text-stone-500 max-w-[200px] truncate">
                        {allocation.notes || "-"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-muted-foreground">No allocations yet. Create your first one above!</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AllocationCalculator;